import { success } from '@redux-requests/core';
import { put, takeEvery, takeLeading, select } from 'redux-saga/effects';
import { app, getDisplayedDifferences } from 'store';
import { fetchAppIntegrations } from './requests';
import { Action } from '@reduxjs/toolkit';
import { isReportsUrl, isResultUrl } from 'utils/location';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { Difference, DocumentTypes } from 'types';
import { Core } from '@pdftron/webviewer';

function* handleFetchAppIntegrations(action: any) {
  yield put(app.actions.setIntegrations(action.payload.data));
}

interface FetchShowLiveTextPayload extends Action {
  payload: boolean;
}

function* handleShowLiveText(action: FetchShowLiveTextPayload) {
  const showLiveText = action.payload;
  if (showLiveText) {
    PDFTronManager.showLiveText(DocumentTypes.source);
    PDFTronManager.showLiveText(DocumentTypes.target);
  } else {
    PDFTronManager.removeLiveText(DocumentTypes.source);
    PDFTronManager.removeLiveText(DocumentTypes.target);
  }
}

interface TextHighlightsPayload extends Action {
  payload: boolean;
}

function* handleUpdateTextHighlights(action: TextHighlightsPayload) {
  const showTextHighlights = action.payload;

  // Only allow showing text highlights on Results/Reports; always hide on Prep
  const isNotPrep = isResultUrl() || isReportsUrl();

  const sourceViewer = PDFManagerFactory.getViewer(DocumentTypes.source);
  const targetViewer = PDFManagerFactory.getViewer(DocumentTypes.target);
  const displayedDifferences: Difference[] = yield select(getDisplayedDifferences);
  const displayedDifferenceIds = displayedDifferences.map((diff: { id: string }) => diff.id);

  [sourceViewer, targetViewer].forEach((viewer) => {
    if (!viewer) return;

    const textHighlightAnnots = viewer.instance.Core.annotationManager
      .getAnnotationsList()
      .filter((annot: Core.Annotations.Annotation) => {
        if (!annot.Id || !(annot.Id.endsWith('-text-highlight') || annot.getCustomData('isTextHighlight') === 'true')) {
          return false;
        }
        // Get the parent difference ID from the text highlight ID by removing '-text-highlight'
        const parentId = annot.Id.replace('-text-highlight', '');
        return displayedDifferenceIds.includes(parentId);
      });

    if (textHighlightAnnots.length > 0) {
      if (showTextHighlights && isNotPrep) {
        // Do not show any text highlights that have been tagged hidden by resync tool
        const toShow = textHighlightAnnots.filter(
          (a: Core.Annotations.Annotation) => a.getCustomData('resyncHidden') !== 'true',
        );
        if (toShow.length > 0) {
          viewer.instance.Core.annotationManager.showAnnotations(toShow);
        }
      } else {
        viewer.instance.Core.annotationManager.hideAnnotations(textHighlightAnnots);
      }
      viewer.instance.Core.documentViewer.refreshAll();
      viewer.instance.Core.documentViewer.updateView();
    }
  });
}

export default [
  takeLeading(success(fetchAppIntegrations.type), handleFetchAppIntegrations),
  takeEvery(app.actions.setShowLiveText.type, handleShowLiveText),
  takeEvery(app.actions.setShowTextHighlights.type, handleUpdateTextHighlights),
];
